import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkle, Lightning } from "@phosphor-icons/react";

export default function AIAssistant() {
  const [prompt, setPrompt] = useState("Give me 3 tips for building great dashboards.");
  const [text, setText] = useState("");
  const [answer, setAnswer] = useState("");
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  const ask = async () => {
    setLoading(true);
    setAnswer("");
    try {
      const { data } = await api.post("/ai/ask", { prompt });
      setAnswer(data.answer);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const summarize = async () => {
    setLoading(true);
    setSummary("");
    try {
      const { data } = await api.post("/ai/summarize", { text });
      setSummary(data.summary);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <header className="mb-6 pb-6 border-b border-[#262626]">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Intelligence</div>
        <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
          AI Assistant <Sparkle size={28} weight="fill" className="text-[#0055FF]" />
        </h1>
        <p className="text-sm text-[#A1A1AA] mt-2">
          Powered by <span className="font-mono text-white">gpt-5.2</span> via Emergent Universal Key. Rate-limited to 30 requests/hour.
        </p>
      </header>

      <Tabs defaultValue="ask">
        <TabsList className="rounded-sm bg-[#141414] border border-[#262626]">
          <TabsTrigger value="ask" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-ask">Ask</TabsTrigger>
          <TabsTrigger value="summarize" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-summarize">Summarize</TabsTrigger>
        </TabsList>

        <TabsContent value="ask" className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="rounded-sm bg-[#141414] border-[#262626] p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-3">Prompt</div>
            <Textarea rows={8} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="rounded-sm bg-[#0A0A0A] border-[#262626] font-mono text-sm" data-testid="ai-prompt-input" />
            <Button onClick={ask} disabled={loading || !prompt.trim()} className="mt-4 rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="ai-ask-btn">
              <Lightning size={14} weight="bold" className="mr-2" /> {loading ? "Thinking..." : "Ask GPT-5.2"}
            </Button>
          </Card>
          <Card className="rounded-sm bg-[#141414] border-[#262626] p-6 min-h-[300px]">
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-3">Response</div>
            {loading ? (
              <div className="space-y-2 animate-pulse">
                <div className="h-3 bg-[#262626] w-full" />
                <div className="h-3 bg-[#262626] w-3/4" />
                <div className="h-3 bg-[#262626] w-5/6" />
              </div>
            ) : answer ? (
              <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-answer">{answer}</div>
            ) : (
              <div className="text-sm text-[#71717A]">Ask something to see the response here.</div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="summarize" className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="rounded-sm bg-[#141414] border-[#262626] p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-3">Text to summarize</div>
            <Textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste any text..." className="rounded-sm bg-[#0A0A0A] border-[#262626] text-sm" data-testid="ai-text-input" />
            <Button onClick={summarize} disabled={loading || !text.trim()} className="mt-4 rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="ai-summarize-btn">
              <Lightning size={14} weight="bold" className="mr-2" /> {loading ? "Summarizing..." : "Summarize"}
            </Button>
          </Card>
          <Card className="rounded-sm bg-[#141414] border-[#262626] p-6 min-h-[300px]">
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-3">Summary</div>
            {summary ? (
              <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-summary">{summary}</div>
            ) : (
              <div className="text-sm text-[#71717A]">Paste text and click Summarize.</div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
